import { useRouter } from "@tanstack/react-router";
import { useEffect } from "react";

/**
 * 외부 링크(메일, 메신저, 웹 등)에서 PWA가 실행될 때의 진입 라우팅 브리지.
 *
 * manifest.launch_handler.client_mode = ["navigate-existing", "auto"] 설정과 함께 동작한다.
 *
 * - 앱이 닫혀 있던 경우: PWA가 새로 떠서 targetURL 경로로 직접 시작 → 별도 처리 없이 라우터가 매칭.
 * - 앱이 이미 떠 있던 경우: 기존 창이 재사용되며 launchQueue 콜백으로 targetURL이 전달됨 → 여기서 navigate.
 *
 * launchQueue API 미지원 브라우저(iOS Safari, Firefox 등)에서는 자동으로 no-op.
 */
export function PwaLaunchBridge() {
  const router = useRouter();

  useEffect(
    function consumeLaunchQueue() {
      const launchQueue = (window as Window & { launchQueue?: LaunchQueue }).launchQueue;
      if (!launchQueue) return;

      launchQueue.setConsumer((launchParams) => {
        const targetURL = launchParams.targetURL;
        if (!targetURL) return;

        const target = new URL(targetURL);
        const isSameOrigin = target.origin === window.location.origin;
        if (!isSameOrigin) return;

        const destination = `${target.pathname}${target.search}${target.hash}`;
        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (destination === current) return;

        router.navigate({ to: destination });
      });
    },
    [router],
  );

  return null;
}

interface LaunchParams {
  readonly targetURL?: string;
  readonly files?: ReadonlyArray<FileSystemHandle>;
}

interface LaunchQueue {
  setConsumer(consumer: (launchParams: LaunchParams) => void): void;
}
